import { Grid, List, Paper } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { fromJS } from 'immutable';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import type { WrappedFieldArrayProps } from 'redux-form';

import FormTextFieldRedesign from 'retired/FormTextFieldRedesign';
import DeedsApi from 'entities/deed/api';

import Option from './Option';
import ContentListItem from './ContentListItem';

type Props = WrappedFieldArrayProps & {
  required?: boolean;
  limit?: number;
  deedTypes: string[];
  variant?: 'standard' | 'outlined' | 'filled';
  sortable?: boolean;
};

const FormContentSelector = ({
  fields,
  meta,
  required = false,
  limit = Infinity,
  deedTypes,
  variant = 'outlined',
  sortable = true,
}: Props): JSX.Element => {
  const [contentSelectorTouched, setContentSelectorTouched] = useState(false);
  const [options, setOptions] = useState(fromJS([]));
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [autocompleteInputValue, setAutocompleteInputValue] = useState('');
  const updateOptions = _.debounce(
    () => {
      if (searchTerm.length > 2) {
        setLoading(true);
        DeedsApi.fetchDeedsForRules(searchTerm, null, deedTypes).subscribe((results) => {
          setOptions(fromJS(results));
          setLoading(false);
        });
      } else {
        setOptions(fromJS([]));
      }
    },
    333,
    { trailing: true }
  );

  useEffect(() => {
    updateOptions();
  }, [searchTerm]);

  const addNew = (item): void => {
    setContentSelectorTouched(true);

    const isDeedAlreadySelected = fields
      ?.getAll?.()
      ?.toJS?.()
      .find((eachField) => eachField.id === item.get('id'));

    if (fields.length === limit) {
      // eslint-disable-next-line no-alert
      alert(
        limit > 1
          ? `You cannot select more than ${limit} content`
          : `You can only select a single content`
      );
    } else if (isDeedAlreadySelected) {
      // eslint-disable-next-line no-alert
      alert('Deed already selected');
    } else {
      fields.push(item);
      setAutocompleteInputValue('');
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item md={6}>
          <Autocomplete
            disableClearable
            onChange={(_event, content): void => {
              addNew(content);
              setOptions(fromJS([]));
            }}
            onOpen={(): void => updateOptions()}
            getOptionLabel={(option): string => option.get('name')}
            loading={loading}
            placeholder="Search by content name or EIN"
            inputValue={autocompleteInputValue}
            PaperComponent={
              variant === 'standard'
                ? (props) => <Paper {...props} style={{ borderRadius: 8 }} />
                : undefined
            }
            renderInput={(params): JSX.Element => (
              <FormTextFieldRedesign
                {...params}
                required={required}
                label="Search Deeds"
                onBlur={(): void => setContentSelectorTouched(true)}
                onFocus={(): void => setContentSelectorTouched(false)}
                onChange={(e): void => {
                  setAutocompleteInputValue(e.target.value);
                  setSearchTerm(e.target.value);
                }}
                variant={variant}
                InputLabelProps={{ shrink: true }}
                meta={meta}
                alwaysDisplayErrors={contentSelectorTouched}
                disabled={limit && limit < fields.size}
              />
            )}
            renderOption={(option): JSX.Element => (
              <Option content={option} key={option.get('id')} />
            )}
            options={options?.toArray() ?? []}
            noOptionsText="No content found"
            // Workaround to fix the @material-ui Autocomplete known issue: https://stackoverflow.com/questions/60409071/react-material-ui-autocomplete-show-options-with-issue
            filterOptions={(option) => option}
          />
          <p style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.54)' }}>
            Search for the deed you&apos;d like to find by name, organization, location or date
          </p>
        </Grid>
      </Grid>
      <List style={{ maxHeight: 560, overflow: 'auto' }}>
        {fields.map((__, index: number) => {
          const content = fields.get(index);

          if (!content) {
            return null;
          }

          return (
            <ContentListItem
              key={content.get('id')}
              index={index}
              move={fields.move}
              content={content}
              onRemove={() => {
                fields.remove(index);
              }}
              sortable={sortable}
            />
          );
        })}
      </List>
    </>
  );
};

export default FormContentSelector;
